import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CardMedia from '@mui/material/CardMedia';
import Typography from '@mui/material/Typography';
import logo from '../icons/logo.png';
import config from '../config';
import { useAuth } from '../auth/AuthContext';

const allowedEmails = (process.env.REACT_APP_GOOGLE_ALLOWED_EMAILS || '')
  .split(',')
  .map((email) => email.trim().toLowerCase())
  .filter(Boolean);

const Login = () => {
  const [error, setError] = useState('');
  const buttonContainerRef = useRef(null);
  const navigate = useNavigate();
  const { isAuthenticated, refreshSession } = useAuth();

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/', { replace: true });
      return undefined;
    }

    if (!config.googleClientId) {
      setError('Google login is not configured. Set REACT_APP_GOOGLE_CLIENT_ID.');
      return undefined;
    }

    const initializeGoogleLogin = () => {
      if (!window.google?.accounts?.id || !buttonContainerRef.current) {
        return false;
      }

      window.google.accounts.id.initialize({
        client_id: config.googleClientId,
        callback: ({ credential }) => {
          try {
            fetch('/api/auth/google', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              credentials: 'include',
              body: JSON.stringify({ credential }),
            })
              .then(async (response) => {
                const data = await response.json().catch(() => ({}));
                if (!response.ok) {
                  throw new Error(data.message || 'Google login failed.');
                }

                const email = data.user?.email?.toLowerCase();
                if (allowedEmails.length > 0 && email && !allowedEmails.includes(email)) {
                  throw new Error('That Google account is not allowed to access this app.');
                }

                await refreshSession();
                navigate('/', { replace: true });
              })
              .catch((loginError) => {
                setError(loginError.message || 'Google login failed.');
              });
          } catch (loginError) {
            setError(loginError.message || 'Google login failed.');
          }
        },
      });

      buttonContainerRef.current.innerHTML = '';
      window.google.accounts.id.renderButton(buttonContainerRef.current, {
        theme: 'outline',
        size: 'large',
        shape: 'pill',
        width: 320,
        text: 'signin_with',
      });

      return true;
    };

    if (initializeGoogleLogin()) {
      return undefined;
    }

    const timer = window.setInterval(() => {
      if (initializeGoogleLogin()) {
        window.clearInterval(timer);
      }
    }, 250);

    return () => window.clearInterval(timer);
  }, [isAuthenticated, navigate, refreshSession]);

  return (
    <div style={{ padding: 40, display: 'flex', justifyContent: 'center' }}>
      <Card sx={{ maxWidth: 500, textAlign: 'center' }}>
        <div style={{ display: 'flex', justifyContent: 'center' }}>
          <CardMedia
            component="img"
            alt="SiCam Logo"
            style={{ height: '150px', width: 'auto', paddingTop: '20px', textAlign: 'center' }}
            image={logo}
          />
        </div>
        <CardContent sx={{ px: 5, pb: 5 }}>
          <Typography gutterBottom variant="h2" component="div" sx={{ color: 'green', textAlign: 'center' }}>
            SiCam Systems
          </Typography>
          <Typography variant="h4" sx={{ color: 'text', textAlign: 'center', mb: 2 }}>
            Tracking System
          </Typography>
          <Typography sx={{ color: 'text.secondary', textAlign: 'center', mb: 3 }}>
            Sign in with your Google account to continue.
          </Typography>
          <div style={{ display: 'flex', justifyContent: 'center', minHeight: '44px' }}>
            <div ref={buttonContainerRef} />
          </div>
          {error && <p style={{ color: 'red', marginTop: '16px' }}>{error}</p>}
          <Typography sx={{ color: 'grey', textAlign: 'center', mt: 4 }}>
            Copyright 2026 - SiCam Systems
          </Typography>
        </CardContent>
      </Card>
    </div>
  );
};

export default Login;
